import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { usePdf } from '@mikecousins/react-pdf';

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.25;

const clampZoom = (value) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value));

const PDFViewer = (props) => {
    const [page, setPage] = useState(1);
    const [errorMsg, setErrorMsg] = useState(null);
    const [zoom, setZoom] = useState(props.initialZoom ?? 1);
    const [isPanning, setIsPanning] = useState(false);
    const canvasRef = useRef(null);
    const scrollContainerRef = useRef(null);
    const dragStateRef = useRef(null);
    const containerHeight = props.height ?? '100vh';

    const resetViewport = () => {
        const container = scrollContainerRef.current;
        if (!container) {
            return;
        }

        container.scrollTo({ left: 0, top: 0, behavior: 'auto' });
    };

    const updateZoom = (nextZoom) => {
        setZoom(clampZoom(nextZoom));
    };

    const resetView = () => {
        setZoom(props.initialZoom ?? 1);
        resetViewport();
    };

    const handlePointerDown = (event) => {
        const container = scrollContainerRef.current;
        if (!container || event.button !== 0) {
            return;
        }

        dragStateRef.current = {
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            scrollLeft: container.scrollLeft,
            scrollTop: container.scrollTop,
        };

        setIsPanning(true);
        event.currentTarget.setPointerCapture?.(event.pointerId);
    };

    const handlePointerMove = (event) => {
        const container = scrollContainerRef.current;
        const dragState = dragStateRef.current;

        if (!container || !dragState) {
            return;
        }

        event.preventDefault();
        container.scrollLeft = dragState.scrollLeft - (event.clientX - dragState.startX);
        container.scrollTop = dragState.scrollTop - (event.clientY - dragState.startY);
    };

    const handlePointerUp = (event) => {
        if (!dragStateRef.current) {
            return;
        }

        dragStateRef.current = null;
        setIsPanning(false);

        if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }
    };

    const handleWheel = (event) => {
        if (!event.ctrlKey) {
            return;
        }

        event.preventDefault();
        updateZoom(zoom + (event.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP));
    };

    React.useEffect(() => {
        setErrorMsg(null);
        setPage(1);
        setZoom(props.initialZoom ?? 1);
        resetViewport();
    }, [props.URI, props.initialZoom]);

    React.useEffect(() => {
        resetViewport();
    }, [page]);

    const { pdfDocument } = usePdf({
        canvasRef,
        file: props.URI,
        page,
        scale: zoom,
    });

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: containerHeight, border: '1px solid #d7dde5', borderRadius: 8, width: '100%', overflow: 'hidden', background: '#ffffff' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderBottom: '1px solid #d7dde5', flexWrap: 'wrap', background: '#f8fafc' }}>
                <button
                    type="button"
                    disabled={!pdfDocument || page === 1}
                    onClick={() => setPage(page - 1)}
                >
                    Prev
                </button>
                <button
                    type="button"
                    disabled={!pdfDocument || page === pdfDocument.numPages}
                    onClick={() => setPage(page + 1)}
                >
                    Next
                </button>
                <span style={{ minWidth: 90 }}>
                    Page {pdfDocument ? page : 0} / {pdfDocument?.numPages ?? 0}
                </span>
                <button
                    type="button"
                    disabled={zoom <= MIN_ZOOM}
                    onClick={() => updateZoom(zoom - ZOOM_STEP)}
                >
                    Zoom Out
                </button>
                <span style={{ minWidth: 70 }}>{Math.round(zoom * 100)}%</span>
                <button
                    type="button"
                    disabled={zoom >= MAX_ZOOM}
                    onClick={() => updateZoom(zoom + ZOOM_STEP)}
                >
                    Zoom In
                </button>
                <button
                    type="button"
                    onClick={resetView}
                >
                    Reset View
                </button>
               
            </div>

            <div
                ref={scrollContainerRef}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerLeave={handlePointerUp}
                onPointerCancel={handlePointerUp}
                onWheel={handleWheel}
                style={{
                    flex: 1,
                    overflow: 'auto',
                    background: '#e2e8f0',
                    cursor: isPanning ? 'grabbing' : 'grab',
                    touchAction: 'none',
                }}
            >
                <div style={{ minWidth: '100%', minHeight: '100%', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: 16, boxSizing: 'border-box' }}>
                    {errorMsg && <span style={{ color: 'red' }}>{errorMsg}</span>}
                    {!pdfDocument && !errorMsg && <span>Loading...</span>}
                    <canvas ref={canvasRef} style={{ display: pdfDocument ? 'block' : 'none', boxShadow: '0 10px 30px rgba(15, 23, 42, 0.18)', background: '#ffffff' }} />
                </div>
            </div>
        </div>
    );
};

PDFViewer.propTypes = {
    URI: PropTypes.string,
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    initialZoom: PropTypes.number,
};

export default PDFViewer;